package com.oasisfeng.invaluable.dsl

import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.ActorRoute
import com.oasisfeng.invaluable.actor.Commitment
import com.oasisfeng.invaluable.actor.CommitmentJournal
import com.oasisfeng.invaluable.actor.CommitmentRendering
import com.oasisfeng.invaluable.actor.CommitmentSnapshot
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.LinkView

/**
 * Link-native session boundary for actors known to one venue.
 *
 * The durable actor identity stays in [Actor]. This class only keeps local journal cursors and exposes
 * actor-shaped verbs so application code reads as actor action rather than journal plumbing.
 *
 * ## First-principles shape:
 *
 *   ### No central graph.
 *   Commitments are graph-free facts on each actor's own journal chain. A venue ([MetaDsl]) is only
 *   an observation boundary that aggregates the journals it knows into a [CommitmentSnapshot];
 *   it owns no economic state of its own, so the same structure transports through the
 *   projection/observation layer without an extra registry.
 *
 *   ### Authorship is economic, not topological.
 *   Information is discovered, not owned: Link topology records informational causality only,
 *   while "who attached this" is evidenced by the issuers of commitments targeting the link.
 *   Domain DSLs therefore keep content topology separate from actor commitments.
 *
 *   ### Canonical links merge co-discovery.
 *   Two actors attaching the same content under the same cause produce the same Link and simply
 *   co-invest in it; competition happens inside one public link, not via topological forks.
 */
open class MetaDsl(protected val projector: LinkProjector) {

    /** Actors with a local journal cursor known to this venue. */
    fun actors(): List<Actor> = journals.keys.toList()
    /** Complete snapshot of all local actors known to this venue. */
    fun snapshot(): CommitmentSnapshot = CommitmentSnapshot(journals.values.mapNotNull { it.head })
    /** Complete snapshot for selected actors known to this venue. Unknown actors contribute no commitments. */
    fun snapshot(actors: Iterable<Actor>) = CommitmentSnapshot(actors.mapNotNull { journals[it]?.head })
    /** Complete snapshot for one actor known to this venue. */
    fun snapshot(actor: Actor): CommitmentSnapshot = snapshot(listOf(actor))

    /**
     * Resume this venue's local cursor from a complete persisted or replicated actor-log [head].
     *
     * This mutates only session state; it creates no Link topology. An ancestor may advance the cursor and an older
     * head is an idempotent no-op, while a divergent head is rejected so persistence restore cannot silently choose a
     * fork for subsequent authoring.
     */
    fun resume(head: Commitment) {
        val current = journals[head.issuer]?.head
        when {
            current == null -> journals[head.issuer] = CommitmentJournal(head.issuer, projector, head)
            current == head || head in current.ancestry() -> Unit
            current in head.ancestry() -> journals[head.issuer] = CommitmentJournal(head.issuer, projector, head)
            else -> throw IllegalArgumentException("Cannot resume a divergent commitment head without choosing a branch.")
        }
    }

    /** Issue [amount] of this actor's currency into [target] through this venue's local journal cursor. */
    fun Actor.back(target: NestedLinkable, amount: Quantity): Commitment = journal.append(target, amount)
    /**
     * Route [subject] to another actor's attention and back that route.
     *
     * This creates a first-hop actor route. Existing [ActorRoute] chains must continue through [relay], so only
     * the current recipient can create the next hop.
     */
    fun Actor.route(subject: NestedLinkable, to: Actor, amount: Quantity): ActorRoute {
        require(ActorRoute.render(subject.link) == null) { "Use relay(...) to continue an existing actor route." }
        return ActorRoute(subject, to).also { back(it, amount) }
    }
    /** Relay an [ActorRoute] onward. Only the current route recipient can make the next hop. */
    fun Actor.relay(route: ActorRoute, to: Actor, amount: Quantity): ActorRoute {
        require(this == route.recipient) { "Only the route recipient can relay it." }
        return route.relay(to).also { back(it, amount) }
    }

    /** Render a complete commitment snapshot through a domain-specific target renderer. */
    fun <T : LinkView> render(snapshot: CommitmentSnapshot, renderTarget: (NestedLink) -> T?): CommitmentRendering<T>
    = snapshot.renderCommitments(renderTarget)

    /** Local mutable journal cursor for this actor within this venue. */
    internal val Actor.journal: CommitmentJournal; get() = journals.getOrPut(this) { CommitmentJournal(this, projector) }
    private val journals = linkedMapOf<Actor, CommitmentJournal>()
}
